import React, { useState } from "react";
import { format } from "date-fns";
import {
  ChevronRight,
  ChevronDown,
  Copy,
  Calendar,
  FileText,
  X,
  MoreVertical,
  Pencil,
  Check,
  Link2,
  Ban,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BundleUsage } from "@/pages/users/types/user";
import { Session } from "@/pages/sessions/types/session";
import { bundleStatusToVariant } from "@/helpers/bundle-status-to-variant";
import { buildV2ProductBookingLink } from "@/lib/dashboard-url";
import { isV2ProductsEnabled } from "@/lib/v2-flag";
import { getExceptionScheduleLink } from "@/helpers/get-exp-schedule-link";
import {
  getMensHealthFollowUpLinks,
  getMensHealthTherapyLink,
  isMensHealthBundle,
  isMensHealthTconsultBundle,
} from "@/helpers/mens-health-schedule";
import {
  getWomensHealthFollowUpLinks,
  isWomensHealthBundle,
} from "@/helpers/womens-health-schedule";

const DASHBOARD_BASE_URL =
  (import.meta.env.VITE_DASHBOARD_URL as string) ??
  "https://dashboard.rockethealth.app";
import {
  getUsageLinkCopyNotAllowedMessage,
  isUsageLinkCopyAllowed,
} from "@/helpers/is-usage-link-copy-allowed";
import { toast } from "@/hooks/use-toast";
import { HasAccess } from "@/components/common/has-access";
import { ExceptionBadge } from "@/features/exception-logs";
import { useUpdateUsageUser } from "../hooks/use-update-usage-user";
import { useFreeSession } from "../hooks/use-free-session";
import { useUpdateTotalSessions } from "../hooks/use-update-total-sessions";
import { useUsageException } from "../hooks/use-usage-exception";
import { useUpdateBundleUsage } from "../hooks/use-update-bundle";
import { useUpdateBundleUsageStatus } from "../hooks/use-update-bundle-usage-status";
import { useUpdatePreferredProfessional } from "../hooks/use-update-preferred-professional";
import { getUsageProductName } from "../utils/past-professionals";
import { useActiveBundles } from "@/features/coupons/hooks/use-active-bundles";
import useAllProfessionals from "@/features/professionals/hooks/use-all-professional";
import { Professional } from "@/pages/professionals/types/professional";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ExtendBundleDialog } from "./extend-bundle-dialog";
import { AssessmentOutcomeDialog } from "./assessment-outcome-dialog";
import { DivaReportDialog } from "./diva-report-dialog";
import { MarkUnsuccessfulDialog } from "@/features/sessions/ui/mark-unsuccessful-dialog";
import { getCancellationBadges } from "@/features/sessions/utils/cancellation-label";
import { useUnmarkUnsuccessful } from "@/features/sessions/hooks/use-mark-unsuccessful";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { BundleCombobox } from "./bundle-combobox";

interface BundleUsageTableProps {
  bundles: BundleUsage[];
  userId: string;
  onCreatePackClick: () => void;
  onUpdateUtilized?: (params: any) => void;
  onCancelSession?: (sessionId: string) => void;
  onRescheduleSession?: (sessionId: string) => void;
  onCreateDoc?: (sessionId: string) => void;
  onMarkNoShow?: (sessionId: string) => void;
}

export function BundleUsageTable({
  bundles,
  userId,
  onCreatePackClick,
  onUpdateUtilized,
  onCancelSession,
  onRescheduleSession,
  onCreateDoc,
  onMarkNoShow,
}: BundleUsageTableProps) {
  const [expandedBundles, setExpandedBundles] = useState<Set<string>>(
    new Set(),
  );
  const [unsuccessfulDialogSourceId, setUnsuccessfulDialogSourceId] = useState<
    string | null
  >(null);
  const { mutate: unmarkUnsuccessful } = useUnmarkUnsuccessful();
  const [editingUserIds, setEditingUserIds] = useState<Record<string, boolean>>(
    {},
  );
  const [userIdValues, setUserIdValues] = useState<Record<string, string>>({});
  const [editingUsageCount, setEditingUsageCount] = useState<
    Record<string, boolean>
  >({});
  const [usageCountValues, setUsageCountValues] = useState<
    Record<string, number>
  >({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<
    Record<string, boolean>
  >({});
  const [pendingBundleIds, setPendingBundleIds] = useState<
    Record<string, string>
  >({});
  const [editingTotalCount, setEditingTotalCount] = useState<
    Record<string, boolean>
  >({});
  const [totalCountValues, setTotalCountValues] = useState<
    Record<string, number>
  >({});
  const [editingProfessional, setEditingProfessional] = useState<
    Record<string, boolean>
  >({});
  const [professionalSearch, setProfessionalSearch] = useState<
    Record<string, string>
  >({});
  const { mutate: updateUserId } = useUpdateUsageUser();
  const { mutate: addFreeSession } = useFreeSession();
  const { mutate: updateTotalSessions } = useUpdateTotalSessions();
  const { mutate: updateUsageException } = useUsageException();
  const { mutate: updateBundleUsage } = useUpdateBundleUsage();
  const { mutate: updateBundleStatus } = useUpdateBundleUsageStatus();
  const { mutate: updatePreferredProfessional } =
    useUpdatePreferredProfessional();
  const { data: bundlesData, isLoading: isBundlesLoading } = useActiveBundles();
  const { data: professionalsData } = useAllProfessionals();

  const toggleExpand = (bundleId: string) => {
    const newExpanded = new Set(expandedBundles);
    if (newExpanded.has(bundleId)) {
      newExpanded.delete(bundleId);
    } else {
      newExpanded.add(bundleId);
    }
    setExpandedBundles(newExpanded);
  };

  const handleEditUserId = (bundleId: string, currentUserId: string) => {
    setEditingUserIds({ ...editingUserIds, [bundleId]: true });
    setUserIdValues({ ...userIdValues, [bundleId]: currentUserId });
  };

  const handleSaveUserId = (bundleId: string) => {
    const userIdValue = userIdValues[bundleId] || "";
    updateUserId(
      { source_id: bundleId, user_id: userIdValue.trim() || null },
      {
        onSuccess: () => {
          toast({ title: "Updated usage identifier" });
          setEditingUserIds({ ...editingUserIds, [bundleId]: false });
        },
        onError: () => {
          toast({
            title: "Failed to update usage identifier",
            variant: "destructive",
          });
          setEditingUserIds({ ...editingUserIds, [bundleId]: false });
        },
      },
    );
  };

  const handleCancelEditUserId = (bundleId: string, originalUserId: string) => {
    setEditingUserIds({ ...editingUserIds, [bundleId]: false });
    setUserIdValues({ ...userIdValues, [bundleId]: originalUserId });
  };

  const handleEditSessionCounts = (
    bundleId: string,
    currentUsageCount: number,
    currentTotalCount: number,
  ) => {
    setEditingUsageCount({ ...editingUsageCount, [bundleId]: true });
    setUsageCountValues({ ...usageCountValues, [bundleId]: currentUsageCount });
    setTotalCountValues({ ...totalCountValues, [bundleId]: currentTotalCount });
  };

  const handleSaveSessionCounts = (bundleId: string) => {
    const usageCountValue = Math.max(0, usageCountValues[bundleId] ?? 0);
    const totalCountValue = Math.max(0, totalCountValues[bundleId] ?? 0);
    if (onUpdateUtilized) {
      onUpdateUtilized({
        usage_id: bundleId,
        new_utilization_session__count: usageCountValue,
      });
    }
    updateTotalSessions({
      usage_id: bundleId,
      new_total_session_quantity: totalCountValue,
    });
    setEditingUsageCount({ ...editingUsageCount, [bundleId]: false });
  };

  const handleCancelEditSessionCounts = (
    bundleId: string,
    originalUsageCount: number,
    originalTotalCount: number,
  ) => {
    setEditingUsageCount({ ...editingUsageCount, [bundleId]: false });
    setUsageCountValues({ ...usageCountValues, [bundleId]: originalUsageCount });
    setTotalCountValues({ ...totalCountValues, [bundleId]: originalTotalCount });
  };

  const handleBundleChange = (
    bundleId: string,
    newBundleId: string,
    currentBundleId: string,
  ) => {
    if (newBundleId !== currentBundleId) {
      setPendingBundleIds({ ...pendingBundleIds, [bundleId]: newBundleId });
      setConfirmDialogOpen({ ...confirmDialogOpen, [bundleId]: true });
    }
  };

  const confirmBundleChange = (bundleId: string) => {
    const newBundleId = pendingBundleIds[bundleId];
    if (newBundleId) {
      updateBundleUsage({
        usage_id: bundleId,
        new_bundle_id: newBundleId,
      });
      setPendingBundleIds({ ...pendingBundleIds, [bundleId]: "" });
      setConfirmDialogOpen({ ...confirmDialogOpen, [bundleId]: false });
    }
  };

  // "Change professional" confirmation (RH-748): picking a professional opens
  // a dialog with a "this is a transition by Rocket Health" tick. Saving with
  // the tick records the transition the user's "Transition by RH" tag counts.
  const [pendingProfessionalChange, setPendingProfessionalChange] = useState<{
    bundleId: string;
    professionalId: string;
    professionalName: string;
    previousName: string | null;
  } | null>(null);
  const [isTransition, setIsTransition] = useState(false);

  const handlePickProfessional = (
    bundleId: string,
    professional: Professional,
    previous: { id: string; name: string } | null,
  ) => {
    // Re-picking the current professional is a no-op: nothing changes and the
    // server would not record a transition, so don't ask about one.
    if (previous?.id === professional.id) {
      handleCancelEditProfessional(bundleId);
      return;
    }
    // A first assignment has nobody to transition from, so it is never a
    // "Transition by RH": save straight away without the dialog.
    if (!previous) {
      handleSaveProfessional(bundleId, professional.id);
      return;
    }
    setPendingProfessionalChange({
      bundleId,
      professionalId: professional.id,
      professionalName: professional.name,
      previousName: previous?.name ?? null,
    });
    setIsTransition(false);
  };

  const handleConfirmProfessionalChange = () => {
    if (!pendingProfessionalChange) return;
    handleSaveProfessional(
      pendingProfessionalChange.bundleId,
      pendingProfessionalChange.professionalId,
      isTransition,
    );
    setPendingProfessionalChange(null);
  };

  const handleEditProfessional = (bundleId: string) => {
    setEditingProfessional({ ...editingProfessional, [bundleId]: true });
    setProfessionalSearch({ ...professionalSearch, [bundleId]: "" });
  };

  const handleSaveProfessional = (
    bundleId: string,
    professionalId: string | null,
    isTransitionByRH = false,
  ) => {
    updatePreferredProfessional(
      {
        usage_id: bundleId,
        user_id: userId,
        professional_id: professionalId,
        is_transition: isTransitionByRH,
      },
      {
        onSuccess: () => {
          setEditingProfessional({ ...editingProfessional, [bundleId]: false });
        },
        onError: () => {
          setEditingProfessional({ ...editingProfessional, [bundleId]: false });
        },
      },
    );
  };

  const handleCancelEditProfessional = (bundleId: string) => {
    setEditingProfessional({ ...editingProfessional, [bundleId]: false });
    setProfessionalSearch({ ...professionalSearch, [bundleId]: "" });
  };

  const calculateDaysRemaining = (endDate: string) => {
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    return `${day}${getSuffix(day)} ${format(date, "MMM yyyy")}`;
  };

  const getSuffix = (day: number): string => {
    if (day >= 11 && day <= 13) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const formatSessionDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "EEEE, MMMM d, yyyy");
  };

  const formatSessionTime = (start: string, end: string) => {
    const startTime = format(new Date(start), "h:mm a");
    const endTime = format(new Date(end), "h:mm a");
    return `${startTime} - ${endTime}`;
  };

  const getSessionStatusBadge = (session: Session) => {
    if (session.status === "canceled") {
      return (
        <>
          {getCancellationBadges(session).map((badge) => (
            <Badge key={badge.label} className={badge.className}>
              {badge.label}
            </Badge>
          ))}
        </>
      );
    }
    if (session.is_no_show) {
      return <Badge className="bg-gray-500 text-white">No Show</Badge>;
    }
    if (session.status === "active") {
      return <Badge className="bg-green-500 text-white">Active</Badge>;
    }
    return <Badge className="bg-gray-500 text-white">Completed</Badge>;
  };

  const isSessionExpiredOrCompleted = (session: Session) => {
    const now = new Date();
    const endTime = new Date(session.end_time);

    const isExpired = endTime < now;
    const isCompleted =
      isExpired && session.status !== "canceled" && !session.is_no_show;
    return isExpired || isCompleted;
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: `${label} copied to clipboard`,
    });
  };

  const copyRescheduleLink = async (session: Session, isWomensHealth: boolean) => {
    if (session.source_id) {
      try {
        const lowerName = (session.name || "").toLowerCase();
        let sessionType: "individual" | "joint" | undefined;

        if (lowerName.includes("joint")) {
          sessionType = "joint";
        } else if (
          lowerName.includes("individual") ||
          lowerName.includes("partner")
        ) {
          sessionType = "individual";
        } else if (lowerName.includes("couple")) {
          sessionType = "joint";
        }

        const sessionTypeQuery = sessionType
          ? `?session_type=${sessionType}`
          : "";
        const baseUrl = isWomensHealth
          ? DASHBOARD_BASE_URL
          : "https://rockethealth.app";
        const rescheduleLink = `${baseUrl}/reschedule/${session.source_id}${sessionTypeQuery}`;
        copyToClipboard(rescheduleLink, "Reschedule link");
      } catch {
        toast({
          title: "Error",
          description: "Failed to copy reschedule link",
          variant: "destructive",
        });
      }
    } else {
      toast({
        title: "Error",
        description: "Session source_id not available",
        variant: "destructive",
      });
    }
  };

  const formatPrice = (
    payment: { amount: number; currency: string } | null | undefined,
  ) => {
    if (!payment) return "—";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: payment.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(payment.amount / 100);
  };

  const truncateId = (id: string, length: number = 20) => {
    if (id.length <= length) return id;
    return `${id.substring(0, length)}...`;
  };

  return (
    <div className="w-full min-w-0 overflow-x-hidden">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Bundle Usage</h2>
          <p className="text-sm text-gray-600">
            All bundles linked to the user
          </p>
        </div>
        <HasAccess permission="write" area="users">
          <Button
            className="bg-orange-500 text-white hover:bg-orange-600"
            onClick={onCreatePackClick}
          >
            Create Pack
          </Button>
        </HasAccess>
      </div>

      {bundles.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border border-gray-200 rounded-lg">
          No bundles found
        </div>
      ) : (
        <div
          className="border border-gray-200 rounded-lg overflow-x-auto w-full whitespace-nowrap"
          style={{
            WebkitOverflowScrolling: "touch",
            overscrollBehaviorX: "contain",
          }}
        >
          <table className="w-full min-w-[1540px] border-collapse table-fixed">
            <thead>
              <tr className="bg-orange-500 border-b border-orange-600">
                <th className="text-left py-2 px-2 text-xs font-medium text-white w-[380px]">
                  Bundle Name
                </th>
                <th className="text-left py-2 px-2 text-xs font-medium text-white w-[140px]">
                  Product
                </th>
                <th className="text-left py-2 px-2 text-xs font-medium text-white w-[160px]">
                  Date of Purchase
                </th>
                <th className="text-left py-2 px-2 text-xs font-medium text-white w-[140px]">
                  Expiry
                </th>
                <th className="text-left py-2 px-2 text-xs font-medium text-white w-[150px]">
                  Status
                </th>
                <th className="text-left py-2 px-2 text-xs font-medium text-white w-[200px]">
                  Usage
                </th>
                <th className="text-left py-2 px-2 text-xs font-medium text-white w-[140px]">
                  Usage ID
                </th>
                <th className="text-left py-2 px-2 text-xs font-medium text-white w-[180px]">
                  User ID
                </th>
                <th className="text-left py-2 px-2 text-xs font-medium text-white w-[110px]">
                  Price
                </th>
                <th className="text-left py-2 px-2 text-xs font-medium text-white w-[90px]">
                  Extra
                </th>
                <th className="text-left py-2 px-2 text-xs font-medium text-white w-[220px]">
                  Preferred Professional
                </th>
                <th className="text-left py-2 px-2 text-xs font-medium text-white w-[170px]">
                  Exceptions
                </th>
              </tr>
            </thead>
            <tbody>
              {bundles.map((bundle) => {
                const isExpanded = expandedBundles.has(bundle.id);
                const usageProductName = getUsageProductName(bundle);
                const daysRemaining = calculateDaysRemaining(bundle.end_date);
                const utilized = Math.max(
                  0,
                  bundle.utilized_session_quantity +
                    bundle.extra_exception_session,
                );
                const extraSessions =
                  bundle.extra_reschedule_session +
                  bundle.extra_exception_session;
                const bundleUserId = bundle.source_usage_id || userId;
                const isEditingUserId = editingUserIds[bundle.id];
                const isEditingUsageCount = editingUsageCount[bundle.id];
                const isEditingTotalCount = editingTotalCount[bundle.id];
                const currentUtilizedCount = bundle.utilized_session_quantity;
                const currentTotalCount = bundle.total_session_quantity;
                const isMensHealth = isMensHealthBundle(
                  bundle.bundle.standard_id,
                );
                const isTConsult = isMensHealthTconsultBundle(
                  bundle.bundle.standard_id,
                );
                const isWomensHealth = isWomensHealthBundle(
                  bundle.bundle.standard_id,
                );
                const canCopyLinks = isUsageLinkCopyAllowed(bundle.status);
                // product_id isn't on the narrow inline bundle type here, but the
                // usage's bundle carries it at runtime — read it defensively.
                const v2ProductId =
                  (bundle.bundle as { product_id?: string | null })
                    ?.product_id ?? null;
                const hasActiveFollowUp = bundle.sessions?.some(
                  (s: any) =>
                    s.name?.toLowerCase().includes('follow') &&
                    (!s.status || s.status.toLowerCase() === 'active'),
                );

                return (
                  <React.Fragment key={bundle.id}>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-2 overflow-hidden">
                        <div className="flex items-center gap-2 min-w-0">
                          <button
                            onClick={() => toggleExpand(bundle.id)}
                            className="p-1 hover:bg-gray-200 rounded flex-shrink-0"
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                          <HasAccess
                            permission="write"
                            area="users"
                            fallback={
                              <span className="text-xs font-medium truncate min-w-4">
                                {bundle.bundle.name}
                              </span>
                            }
                          >
                            {!isBundlesLoading ? (
                              <BundleCombobox
                                bundles={bundlesData?.bundles ?? []}
                                value={bundle.bundle.id}
                                fallbackLabel={bundle.bundle.name}
                                onSelect={(newBundleId) =>
                                  handleBundleChange(
                                    bundle.id,
                                    newBundleId,
                                    bundle.bundle.id,
                                  )
                                }
                                triggerClassName="h-7 text-xs w-[300px] flex-shrink-0"
                              />
                            ) : (
                              <span className="text-xs font-medium truncate min-w-0">
                                {bundle.bundle.name}
                              </span>
                            )}
                          </HasAccess>
                            <HasAccess permission="write" area="users">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 flex-shrink-0"
                                >
                                  <MoreVertical className="h-4 w-4 text-gray-400" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canCopyLinks && (
                                  () => {
                                    return (
                                      <>
                                        {/* v2 booking link → dashboard product
                                            page for this usage. Only when the v2
                                            flag is on and the bundle has a product. */}
                                        {isV2ProductsEnabled() &&
                                          v2ProductId && (
                                            <DropdownMenuItem
                                              onClick={async () => {
                                                if (
                                                  !isUsageLinkCopyAllowed(
                                                    bundle.status,
                                                  )
                                                ) {
                                                  toast({
                                                    title: "Error",
                                                    description:
                                                      getUsageLinkCopyNotAllowedMessage(
                                                        bundle.status,
                                                      ),
                                                    variant: "destructive",
                                                  });
                                                  return;
                                                }
                                                const link =
                                                  buildV2ProductBookingLink(
                                                    v2ProductId,
                                                    bundle.id,
                                                  );
                                                await navigator.clipboard.writeText(
                                                  link,
                                                );
                                                toast({
                                                  title: "Copied",
                                                  description:
                                                    "V2 booking link copied to clipboard",
                                                });
                                              }}
                                            >
                                              Copy V2 Booking Link
                                            </DropdownMenuItem>
                                          )}
                                      </>
                                    );
                                  })()}
                                {/* {canCopyLinks && isMensHealth && !hasActiveFollowUp && (
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      if (
                                        !isUsageLinkCopyAllowed(bundle.status)
                                      ) {
                                        toast({
                                          title: "Error",
                                          description:
                                            getUsageLinkCopyNotAllowedMessage(
                                              bundle.status,
                                            ),
                                          variant: "destructive",
                                        });
                                        return;
                                      }
                                      const links = getMensHealthFollowUpLinks({
                                        usage_id: bundle.id,
                                      });
                                      await navigator.clipboard.writeText(
                                        links.free,
                                      );
                                      toast({
                                        title: "Copied",
                                        description:
                                          "Free follow-up link copied to clipboard",
                                      });
                                    }}
                                  >
                                    Copy Free Follow-up Link
                                  </DropdownMenuItem>
                                )} */}

                                {/* {canCopyLinks && isMensHealth && (
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      if (
                                        !isUsageLinkCopyAllowed(bundle.status)
                                      ) {
                                        toast({
                                          title: "Error",
                                          description:
                                            getUsageLinkCopyNotAllowedMessage(
                                              bundle.status,
                                            ),
                                          variant: "destructive",
                                        });
                                        return;
                                      }
                                      const links = getMensHealthFollowUpLinks({
                                        usage_id: bundle.id,
                                      });
                                      await navigator.clipboard.writeText(
                                        links.paid,
                                      );
                                      toast({
                                        title: "Copied",
                                        description:
                                          "Paid follow-up link copied to clipboard",
                                      });
                                    }}
                                  >
                                    Copy Paid Follow-up Link
                                  </DropdownMenuItem>
                                )} */}

                                {/* {canCopyLinks && isWomensHealth && !hasActiveFollowUp && (
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      if (
                                        !isUsageLinkCopyAllowed(bundle.status)
                                      ) {
                                        toast({
                                          title: "Error",
                                          description:
                                            getUsageLinkCopyNotAllowedMessage(
                                              bundle.status,
                                            ),
                                          variant: "destructive",
                                        });
                                        return;
                                      }
                                      const links =
                                        getWomensHealthFollowUpLinks({
                                          usage_id: bundle.id,
                                        });
                                      await navigator.clipboard.writeText(
                                        links.free,
                                      );
                                      toast({
                                        title: "Copied",
                                        description:
                                          "Free follow-up link copied to clipboard",
                                      });
                                    }}
                                  >
                                    Copy Free Follow-up Link
                                  </DropdownMenuItem>
                                )}

                                {canCopyLinks && isWomensHealth && (
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      if (
                                        !isUsageLinkCopyAllowed(bundle.status)
                                      ) {
                                        toast({
                                          title: "Error",
                                          description:
                                            getUsageLinkCopyNotAllowedMessage(
                                              bundle.status,
                                            ),
                                          variant: "destructive",
                                        });
                                        return;
                                      }
                                      const links =
                                        getWomensHealthFollowUpLinks({
                                          usage_id: bundle.id,
                                        });
                                      await navigator.clipboard.writeText(
                                        links.paid,
                                      );
                                      toast({
                                        title: "Copied",
                                        description:
                                          "Paid follow-up link copied to clipboard",
                                      });
                                    }}
                                  >
                                    Copy Paid Follow-up Link
                                  </DropdownMenuItem>
                                )} */}

                                {canCopyLinks && isTConsult && (
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      if (
                                        !isUsageLinkCopyAllowed(bundle.status)
                                      ) {
                                        toast({
                                          title: "Error",
                                          description:
                                            getUsageLinkCopyNotAllowedMessage(
                                              bundle.status,
                                            ),
                                          variant: "destructive",
                                        });
                                        return;
                                      }
                                      const therapyLink =
                                        getMensHealthTherapyLink({
                                          standard_id:
                                            bundle.bundle.standard_id,
                                          usage_id: bundle.id,
                                          sessions: bundle?.sessions,
                                        });
                                      if (!therapyLink) {
                                        toast({
                                          title: "Error",
                                          description:
                                            "Therapy link not available",
                                          variant: "destructive",
                                        });
                                        return;
                                      }
                                      await navigator.clipboard.writeText(
                                        therapyLink,
                                      );
                                      toast({
                                        title: "Copied",
                                        description:
                                          "Therapy link copied to clipboard",
                                      });
                                    }}
                                  >
                                    Copy Therapy Link
                                  </DropdownMenuItem>
                                )}

                                {canCopyLinks && (
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      if (
                                        !isUsageLinkCopyAllowed(bundle.status)
                                      ) {
                                        toast({
                                          title: "Error",
                                          description:
                                            getUsageLinkCopyNotAllowedMessage(
                                              bundle.status,
                                            ),
                                          variant: "destructive",
                                        });
                                        return;
                                      }
                                      const schedule_link =
                                        getExceptionScheduleLink({
                                          standard_id:
                                            bundle.bundle.standard_id,
                                          usage_id: bundle.id,
                                          session_event_type:
                                            bundle?.sessions?.[0]?.event_type ||
                                            "",
                                        });
                                      await navigator.clipboard.writeText(
                                        schedule_link,
                                      );
                                      toast({
                                        title: "Copied",
                                        description:
                                          "Exception schedule link copied to clipboard",
                                      });
                                    }}
                                  >
                                    Copy Exception Schedule Link
                                  </DropdownMenuItem>
                                )}

                                {/* Copy links above stay for anyone who can view the user; everything
                                    below changes the bundle (usage routes), so it is judged as Users. */}
                                <HasAccess permission="write" area="users">
                                <DropdownMenuItem
                                  onClick={() =>
                                    updateUsageException(bundle.id)
                                  }
                                >
                                  {bundle.allow_schedule_exception
                                    ? "Disable Exception"
                                    : "Allow Exception"}
                                </DropdownMenuItem>

                                <ExtendBundleDialog
                                  bundleId={bundle.id}
                                  userId={userId}
                                  endDate={bundle.end_date}
                                  trigger={
                                    <DropdownMenuItem
                                      onSelect={(e) => e.preventDefault()}
                                    >
                                      Extend bundle expiry
                                    </DropdownMenuItem>
                                  }
                                />

                                <DropdownMenuItem
                                  onClick={() =>
                                    addFreeSession({
                                      usage_id: bundle.id,
                                      userId: userId,
                                      newSessionCount:
                                        bundle.total_session_quantity + 1,
                                    })
                                  }
                                >
                                  Add free session
                                </DropdownMenuItem>

                                {/* ADHD assessment care-team bridge. Gated by
                                    the bundle's care_team_actions flags (set in
                                    the bundle form): two-session → outcome only,
                                    standalone DIVA → report only, full → both.
                                    Absent → hidden (non-ADHD bundles). */}
                                {bundle.bundle?.metadata?.care_team_actions
                                  ?.assessment_outcome && (
                                  <AssessmentOutcomeDialog
                                    usageId={bundle.id}
                                    currentOutcome={
                                      bundle.metadata?.assessment_outcome
                                    }
                                    currentNotes={
                                      bundle.metadata?.assessment_notes
                                    }
                                    trigger={
                                      <DropdownMenuItem
                                        onSelect={(e) => e.preventDefault()}
                                      >
                                        Record exploration outcome
                                      </DropdownMenuItem>
                                    }
                                  />
                                )}
                                {bundle.bundle?.metadata?.care_team_actions
                                  ?.diva_report && (
                                  <DivaReportDialog
                                    usageId={bundle.id}
                                    reportSaved={
                                      !!bundle.metadata?.diva_report_key
                                    }
                                    currentOutcome={
                                      bundle.metadata?.diva_outcome
                                    }
                                    currentScores={bundle.metadata?.diva_scores}
                                    reportType={
                                      bundle.bundle?.metadata?.care_team_actions
                                        ?.report_type
                                    }
                                    trigger={
                                      <DropdownMenuItem
                                        onSelect={(e) => e.preventDefault()}
                                      >
                                        {bundle.bundle?.metadata
                                          ?.care_team_actions?.report_type ===
                                        "ipde"
                                          ? "IPDE report"
                                          : "DIVA report"}
                                      </DropdownMenuItem>
                                    }
                                  />
                                )}
                                </HasAccess>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            </HasAccess>
                        </div>
                      </td>
                      <td className="py-2 px-2 overflow-hidden">
                        {usageProductName ? (
                          <div className="text-xs text-gray-700 truncate">
                            {usageProductName}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-xs text-gray-700 overflow-hidden">
                        <div className="truncate">
                          {bundle.payment?.created_at
                            ? formatDate(bundle.payment.created_at)
                            : "—"}
                        </div>
                      </td>
                      <td className="py-2 px-2 text-xs text-gray-700 overflow-hidden">
                        <div className="truncate">
                          {formatDate(bundle.end_date)}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {daysRemaining > 0
                            ? `${daysRemaining} days remaining`
                            : "0 days remaining"}
                        </div>
                      </td>
                      <td className="py-2 px-2 overflow-hidden">
                        <HasAccess
                          permission="write"
                          area="users"
                          fallback={
                            <Badge
                              variant={bundleStatusToVariant(bundle.status)}
                              className="text-xs"
                            >
                              {bundle.status}
                            </Badge>
                          }
                        >
                          <Select
                            value={bundle.status}
                            onValueChange={(newStatus) =>
                              updateBundleStatus({
                                bundle_id: bundle.id,
                                status: newStatus,
                              })
                            }
                          >
                            <SelectTrigger className="h-10 text-xs w-[130px]">
                              <SelectValue>
                                <Badge
                                  variant={bundleStatusToVariant(bundle.status)}
                                  className="text-xs"
                                >
                                  {bundle.status}
                                </Badge>
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                              <SelectItem value="COMPLETED">
                                COMPLETED
                              </SelectItem>
                              <SelectItem value="EXPIRED">EXPIRED</SelectItem>
                              <SelectItem value="INACTIVE">INACTIVE</SelectItem>
                              <SelectItem value="FULLY_REFUNDED">
                                FULLY_REFUNDED
                              </SelectItem>
                              <SelectItem value="COMPLETED_WITH_PARTIAL_REFUND">
                                COMPLETED_WITH_PARTIAL_REFUND
                              </SelectItem>
                              <SelectItem value="TRANSFERRED">
                                TRANSFERRED
                              </SelectItem>
                              <SelectItem value="CONVERTED">CONVERTED</SelectItem>
                            </SelectContent>
                          </Select>
                        </HasAccess>
                      </td>
                      <td className="py-2 px-2 text-xs text-gray-700 overflow-hidden">
                        {isEditingUsageCount ? (
                          <div className="flex items-center gap-1 min-w-0">
                            <Input
                              type="number"
                              value={
                                usageCountValues[bundle.id] ??
                                currentUtilizedCount
                              }
                              onChange={(e) => {
                                const value = Number(e.target.value);
                                setUsageCountValues({
                                  ...usageCountValues,
                                  [bundle.id]: Math.max(0, value),
                                });
                              }}
                              className="h-7 text-xs w-14 flex-shrink-0"
                              autoFocus
                              min={0}
                            />
                            <span className="text-xs text-gray-400 flex-shrink-0">/</span>
                            <Input
                              type="number"
                              value={
                                totalCountValues[bundle.id] ??
                                currentTotalCount
                              }
                              onChange={(e) => {
                                const value = Number(e.target.value);
                                setTotalCountValues({
                                  ...totalCountValues,
                                  [bundle.id]: Math.max(0, value),
                                });
                              }}
                              className="h-7 text-xs w-14 flex-shrink-0"
                              min={0}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 flex-shrink-0"
                              onClick={() => handleSaveSessionCounts(bundle.id)}
                            >
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 flex-shrink-0"
                              onClick={() =>
                                handleCancelEditSessionCounts(
                                  bundle.id,
                                  currentUtilizedCount,
                                  currentTotalCount,
                                )
                              }
                            >
                              <X className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="text-xs text-gray-700 truncate min-w-0">
                              {utilized}/{currentTotalCount}
                            </span>
                            <HasAccess permission="write" area="users">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 flex-shrink-0"
                                onClick={() =>
                                  handleEditSessionCounts(
                                    bundle.id,
                                    currentUtilizedCount,
                                    currentTotalCount,
                                  )
                                }
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                            </HasAccess>
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-2 overflow-hidden">
                        <div className="flex items-center gap-1 min-w-0">
                          <span className="text-xs text-gray-700 font-mono truncate min-w-0">
                            {truncateId(bundle.id)}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 flex-shrink-0"
                            onClick={() =>
                              copyToClipboard(bundle.id, "Usage ID")
                            }
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                      <td className="py-2 px-2 overflow-hidden">
                        {isEditingUserId ? (
                          <div className="flex items-center gap-1 min-w-0">
                            <Input
                              value={userIdValues[bundle.id] || bundleUserId}
                              onChange={(e) =>
                                setUserIdValues({
                                  ...userIdValues,
                                  [bundle.id]: e.target.value,
                                })
                              }
                              className="h-7 text-xs w-24 flex-shrink-0"
                              autoFocus
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 flex-shrink-0"
                              onClick={() => handleSaveUserId(bundle.id)}
                            >
                              <Check className="h-3 w-3 text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 flex-shrink-0"
                              onClick={() =>
                                handleCancelEditUserId(bundle.id, bundleUserId)
                              }
                            >
                              <X className="h-3 w-3 text-red-600" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="text-xs text-gray-700 font-mono truncate min-w-0">
                              {truncateId(bundleUserId)}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 flex-shrink-0"
                              onClick={() =>
                                copyToClipboard(bundleUserId, "User ID")
                              }
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                            <HasAccess permission="write" area="users">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 flex-shrink-0"
                                onClick={() =>
                                  handleEditUserId(bundle.id, bundleUserId)
                                }
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                            </HasAccess>
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-2 text-xs text-gray-700 overflow-hidden">
                        <div className="truncate">
                          {formatPrice(bundle.payment)}
                        </div>
                      </td>
                      <td className="py-2 px-2 text-xs text-gray-700 overflow-hidden">
                        {extraSessions}
                      </td>
                      <td className="py-2 px-2 overflow-hidden">
                        {editingProfessional[bundle.id] ? (
                          <Popover
                            open={true}
                            onOpenChange={(open) => {
                              if (!open)
                                handleCancelEditProfessional(bundle.id);
                            }}
                          >
                            <PopoverTrigger asChild>
                              <button className="text-xs text-gray-700 underline">
                                {bundle.professional?.name ?? "N/A"}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-64 p-2">
                              <Input
                                placeholder="Search professional..."
                                value={professionalSearch[bundle.id] || ""}
                                onChange={(e) =>
                                  setProfessionalSearch({
                                    ...professionalSearch,
                                    [bundle.id]: e.target.value,
                                  })
                                }
                                className="h-7 text-xs mb-2"
                                autoFocus
                              />
                              <div className="max-h-48 overflow-y-auto space-y-0.5">
                                <button
                                  className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-gray-100 text-gray-500 italic"
                                  onClick={() =>
                                    handleSaveProfessional(bundle.id, null)
                                  }
                                >
                                  Clear (N/A)
                                </button>
                                {(professionalsData?.professionals ?? [])
                                  .filter((p: Professional) =>
                                    p.name
                                      .toLowerCase()
                                      .includes(
                                        (
                                          professionalSearch[bundle.id] || ""
                                        ).toLowerCase(),
                                      ),
                                  )
                                  .map((p: Professional) => {
                                    const isCurrent =
                                      p.id === bundle.professional?.id;
                                    return (
                                      <button
                                        key={p.id}
                                        className={`w-full text-left text-xs px-2 py-1.5 rounded flex items-center justify-between gap-2 ${
                                          isCurrent
                                            ? "text-gray-400 cursor-default"
                                            : "hover:bg-gray-100"
                                        }`}
                                        onClick={() =>
                                          handlePickProfessional(
                                            bundle.id,
                                            p,
                                            bundle.professional
                                              ? {
                                                  id: bundle.professional.id,
                                                  name: bundle.professional.name,
                                                }
                                              : null,
                                          )
                                        }
                                      >
                                        <span>{p.name}</span>
                                        {isCurrent && (
                                          <span className="text-[10px] uppercase tracking-wide">
                                            Current
                                          </span>
                                        )}
                                      </button>
                                    );
                                  })}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="text-xs text-gray-700 truncate">
                              {bundle.professional?.name ?? "N/A"}
                            </span>
                            <HasAccess permission="write" area="users">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 flex-shrink-0"
                                onClick={() =>
                                  handleEditProfessional(bundle.id)
                                }
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                            </HasAccess>
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-2 overflow-hidden">
                        {bundle.exceptionLogs &&
                        bundle.exceptionLogs.length > 0 ? (
                          <ExceptionBadge
                            exceptionLogs={bundle.exceptionLogs}
                            side="left"
                          />
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                    <HasAccess permission="write" area="users">
                      <Dialog
                        open={confirmDialogOpen[bundle.id] || false}
                        onOpenChange={(open) =>
                          setConfirmDialogOpen({
                            ...confirmDialogOpen,
                            [bundle.id]: open,
                          })
                        }
                      >
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Confirm Bundle Change</DialogTitle>
                          </DialogHeader>
                          <div className="py-4">
                            Are you sure you want to change this usage to a
                            different bundle?
                          </div>
                          <DialogFooter>
                            <Button
                              variant="outline"
                              onClick={() =>
                                setConfirmDialogOpen({
                                  ...confirmDialogOpen,
                                  [bundle.id]: false,
                                })
                              }
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={() => confirmBundleChange(bundle.id)}
                            >
                              Confirm Change
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </HasAccess>
                    {isExpanded &&
                      bundle.sessions &&
                      bundle.sessions.length > 0 && (
                        <tr>
                          <td colSpan={13} className="p-0">
                            <div className="bg-gray-50 px-4 py-4 w-full min-w-0 overflow-hidden">
                              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                                Sessions
                              </h3>
                              <div className="space-y-4 w-full min-w-0">
                                {[...bundle.sessions]
                                  .sort((a: Session, b: Session) => {
                                    const aTime = a.start_time
                                      ? new Date(a.start_time).getTime()
                                      : 0;
                                    const bTime = b.start_time
                                      ? new Date(b.start_time).getTime()
                                      : 0;
                                    return bTime - aTime;
                                  })
                                  .map((session: Session) => {
                                  const shouldFade =
                                    isSessionExpiredOrCompleted(session);
                                  return (
                                    <div
                                      key={session.id}
                                      className={`bg-white rounded-lg p-4 border border-gray-200 space-y-3 w-full min-w-0 overflow-hidden ${shouldFade ? "opacity-50" : ""}`}
                                    >
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-sm font-medium">
                                          {session.name}
                                        </span>
                                        {getSessionStatusBadge(session)}
                                        {session.is_unsuccessful && (
                                          <Badge className="bg-amber-600 text-white">
                                            Unsuccessful
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-600 mb-2">
                                        Usage ID:{" "}
                                        {session.usage_identifier ||
                                          session.source_id ||
                                          bundle.id}
                                      </div>
                                        <div>
                                          <div className="text-xs font-semibold text-gray-700 mb-2">
                                            QUICK ACTIONS
                                          </div>
                                          <div className="flex gap-2 flex-wrap">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="h-7 text-xs bg-white text-blue-600 border-blue-600 hover:bg-blue-50"
                                              onClick={() =>
                                                copyRescheduleLink(session, isWomensHealth)
                                              }
                                              disabled={
                                                !session.reschedule_link
                                              }
                                            >
                                              <Link2 className="h-3 w-3 mr-1" />
                                              Copy Reschedule Link
                                            </Button>
                                            {/* Copy stays for anyone who can view; the rest act on the SESSION,
                                                so they are judged as Sessions wherever this table is rendered. */}
                                            <HasAccess permission="write" area="sessions">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="h-7 text-xs bg-white text-red-600 border-red-600 hover:bg-red-50"
                                              onClick={() =>
                                                onCancelSession?.(session.id)
                                              }
                                              disabled={
                                                session.status === "canceled"
                                              }
                                            >
                                              <X className="h-3 w-3 mr-1" />
                                              Cancel Session
                                            </Button>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="h-7 text-xs bg-white text-green-600 border-green-600 hover:bg-green-50"
                                              onClick={() =>
                                                onRescheduleSession?.(
                                                  session.id,
                                                )
                                              }
                                              disabled={
                                                session.status === "canceled"
                                              }
                                            >
                                              <Calendar className="h-3 w-3 mr-1" />
                                              Reschedule
                                            </Button>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="h-7 text-xs bg-white text-orange-600 border-orange-600 hover:bg-orange-50"
                                              onClick={() =>
                                                onCreateDoc?.(session.id)
                                              }
                                              disabled={
                                                session.status === "canceled"
                                              }
                                            >
                                              <FileText className="h-3 w-3 mr-1" />
                                              Create Doc
                                            </Button>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="h-7 text-xs bg-white text-gray-600 border-gray-600 hover:bg-gray-50"
                                              onClick={() =>
                                                onMarkNoShow?.(session.id)
                                              }
                                              disabled={
                                                session.status === "canceled"
                                              }
                                            >
                                              <X className="h-3 w-3 mr-1" />
                                              {session.is_no_show
                                                ? "Undo Mark No Show"
                                                : "Mark No Show"}
                                            </Button>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="h-7 text-xs bg-white text-amber-700 border-amber-600 hover:bg-amber-50"
                                              onClick={() => {
                                                if (session.is_unsuccessful) {
                                                  unmarkUnsuccessful({
                                                    source_id:
                                                      session.source_id,
                                                  });
                                                } else {
                                                  setUnsuccessfulDialogSourceId(
                                                    session.source_id,
                                                  );
                                                }
                                              }}
                                            >
                                              <Ban className="h-3 w-3 mr-1" />
                                              {session.is_unsuccessful
                                                ? "Unmark Unsuccessful"
                                                : "Mark as Unsuccessful"}
                                            </Button>
                                            </HasAccess>
                                          </div>
                                        </div>
                                        {/* Divider line below Quick Actions */}
                                        <div className="border-t border-gray-200 mt-3 mb-3"></div>
                                      {session.professional ? (
                                        <div className="text-xs mb-2">
                                          <span className="font-semibold text-gray-700">
                                            Assigned Professional:{" "}
                                          </span>
                                          <span className="text-purple-600 font-medium">
                                            {session.professional.name || "N/A"}
                                            {session.professional.department &&
                                              ` • ${session.professional.department}`}
                                          </span>
                                        </div>
                                      ) : session.professional_id ? (
                                        <div className="text-xs text-gray-500 mb-2">
                                          <span className="font-semibold">
                                            Assigned Professional:{" "}
                                          </span>
                                          <span>Not available</span>
                                        </div>
                                      ) : null}
                                      <div className="text-sm text-gray-700">
                                        <div>
                                          {formatSessionDate(
                                            session.start_time,
                                          )}
                                        </div>
                                        <div>
                                          {formatSessionTime(
                                            session.start_time,
                                            session.end_time,
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <MarkUnsuccessfulDialog
        source_id={unsuccessfulDialogSourceId ?? ""}
        open={!!unsuccessfulDialogSourceId}
        onOpenChange={(open) => {
          if (!open) setUnsuccessfulDialogSourceId(null);
        }}
      />

      <Dialog
        open={pendingProfessionalChange !== null}
        onOpenChange={(open) => {
          if (!open) setPendingProfessionalChange(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change professional</DialogTitle>
            <DialogDescription>
              {pendingProfessionalChange?.previousName
                ? `${pendingProfessionalChange.previousName} → ${pendingProfessionalChange.professionalName}`
                : `Assign ${pendingProfessionalChange?.professionalName ?? ""}`}
            </DialogDescription>
          </DialogHeader>
          <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm">
            <Checkbox
              checked={isTransition}
              onCheckedChange={(checked) => setIsTransition(checked === true)}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium">
                This is a transition by Rocket Health
              </span>
              <span className="mt-0.5 block text-xs text-gray-500">
                Tick when Rocket Health is moving the user, for example because
                the professional was dismissed or left. Leave it unticked for a
                change the user asked for. Ticked changes add to the user's
                "Transition by RH" tag.
              </span>
            </span>
          </label>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingProfessionalChange(null)}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmProfessionalChange}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
